<template>
  <div>
    <header class="header">
      <button :class="{ active: menu }"
        @click.prevent="menu = !menu"
        class="hamburger hamburger--collapse"
        type="button">

        <span class="hamburger-box">
          <span class="hamburger-inner">&#9776;</span>
        </span>
      </button>

      <span class="month-year">
        Rogel Cancer Center Events
      </span>
    </header>

    <Menu
      :menu="menu"
      :menus="calendars"
      :loading="loading"
      @toggle-item="toggleCalendarItem">
    </Menu>

    <div class="events-template">
      <div class="month-selector">
        <transition name="fade">
          <a
            href="#"
            class="icon-left-open-big month-selector-arrow prev"
            @click="changeMonth('prev')"
            v-show="currentMonth != minMonth"
            v-if="!loading">
          </a>
        </transition>

        <div>{{ month }}</div>

        <transition name="fade">
          <a
            href="#"
            class="icon-right-open-big month-selector-arrow next"
            @click="changeMonth('next')"
            v-show="currentMonth != maxMonth"
            v-if="!loading">
          </a>
        </transition>
      </div>

      <Spinner v-if="loading"></Spinner>

      <div class="day-list-wrapper">
        <div
          class="no-results"
          v-if="noResults">No events found. Please try another month or
            select different calendars from the menu.
        </div>

        <transition name="fade">
          <ul
            class="day-list"
            v-show="events.length > 0">

            <li
              v-for="day in sortedDays"
              :key="day.day">

              <div class="day">
                <span class="day-integer">{{ day.day }}</span> {{ day.dayWeek }}
              </div>

              <ul class="event-list">
                <transition-group name="fade">
                  <li
                    class="event"
                    v-for="event in sortedEvents"
                    :key="event.id"
                    v-if="eventActive(event, day)">

                    <Event :event="event"></Event>
                  </li>
                </transition-group>
              </ul>
            </li>
          </ul>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
  import mixins from "../mixins.js";
  import { DateTime } from "luxon";
  import Menu from "../components/Menu.vue";
  import Event from "../components/Event.vue";
  import Spinner from "../components/Spinner.vue";

  export default {
    data() {
      return {
        calendars: {},
        days: [],
        month: "",
        events: [],
        minMonth: "",
        maxMonth: "",
        noResults: false,
        currentMonth: "",
        currentYearOnly: "",
        currentMonthOnly: "",
        menu: false,
        eventsLoaded: false
      }
    },

    components: {
      Event,
      Menu,
      Spinner
    },

    mixins: [mixins],

    props: ["api", "loading"],

    computed: {
      sortedDays() {
        return this.days.sort((a, b) => {
          return a.day - b.day;
        });
      },

      sortedEvents() {
        return this.events.sort((a, b) => {
          if (a.start24 < b.start24) {
            return -1;
          }

          if (a.start24 > b.start24) {
            return 1;
          }

          return 0;
        });
      },

      checkForResults() {
        this.noResults = this.days > 0 ? false : true;
      }
    },

    mounted() {
      // Get the current month and year.
      const month = DateTime.local().toFormat("LL");
      const year = DateTime.local().toFormat("y");
      const nextYear = DateTime.local().plus({ year: 1 }).toFormat("y");

      // Fetch calendars.
      const calendarPromise = this.getCalendars();

      calendarPromise.then((fetchedCalendars) => {
        return this.parseCalendarData(fetchedCalendars);
      })

      // Store calendar data in vue object.
      .then((parsedCalendarData) => {
        this.calendars = parsedCalendarData;
        return this.calendars;
      })

      // Check status of calendars.
      .then((calendars) => {
        this.checkCalendarsStatus(calendars);
      })

      // Grab events after calendars have been fetched.
      .then(() => {
        const url = this.api + "/api/node/event?filter[event_month][value]="
        + month + "&filter[event_year][value]=" + year;

        // Set current month and year.
        this.currentMonth = year + month;
        this.currentMonthOnly = month;
        this.currentYearOnly = year;

        // Set current month and year for display.
        this.month = DateTime.local().toFormat("LLLL y");

        // Set the min and max months available.
        this.minMonth = year + month;
        this.maxMonth = nextYear + month;

        // Grab events.
        const eventsPromise = this.getEvents(url);

        // Promise that you won't move on until you have all the events.
        eventsPromise.then((events) => {
          this.parseEventData(events);
        })

        // Add days.
        .then(() => {
          this.addDays(this.events);
        })

        // Check for no results.
        .then(() => {
          this.noResults = this.days.length > 0 ? false : true;
        })

        // Disable loading.
        .then(() => {
          this.$emit("disable-loading");
        });

      });
    },

    methods: {
      eventActive(event, day) {
        if (event.day == day.day
          && this.calendars[event.calendar].checked
          && (event.month + " " + event.year) === this.month) {

          return true;
        }
      },

      addDays(events, days = []) {
        // Create days that have events in the month.
        events.forEach((event) => {
          if (this.calendars[event.calendar].checked) {

            // Check to see if the object already exists in the array.
            if (this.days.find((obj) =>
              { return obj.day === event.day }) === undefined) {

              this.days.push({
                day: event.day,
                dayWeek: event.dayWeek
              });
            }
          }
        });
      },

      toggleCalendarItem() {
        // Disable menu
        this.menu = false;

        // Enable loading.
        this.$emit("enable-loading");

        // Reset events array.
        this.events = [];

        // Reset the days array.
        this.days = [];

        // Fetch new events.
        const url = this.api + "/api/node/event?filter[event_month][value]="
          + this.currentMonthOnly + "&filter[event_year][value]=" + this.currentYearOnly;

        const eventsPromise = this.getEvents(url);

        eventsPromise.then((events) => {
          this.parseEventData(events);
        })

        // Add days.
        .then(() => {
          this.addDays(this.events);
        })

        // Check for no results.
        .then(() => {
          this.noResults = this.days.length > 0 ? false : true;
        })

        // Disable loading.
        .then(() => {
          this.$emit("disable-loading");
        });
      },

      // Changes the month and fetches new data from the API.
      // Provide either next or prev to change the month accordingly.
      changeMonth(direction = next) {
        let newDate = {};

        // Disable menu.
        this.menu = false;

        // Set loading true to show animation.
        this.$emit("enable-loading");

        if (direction === "next") {
          newDate = DateTime.fromISO(this.currentMonth).plus({ month: 1 });
        }

        if (direction === "prev") {
          newDate = DateTime.fromISO(this.currentMonth).minus({ month: 1 });
        }

        const newMonth = newDate.toFormat("LL");
        const newYear = newDate.toFormat("y");

        // Set new values.
        this.month = newDate.toFormat("LLLL y");
        this.currentMonth = newDate.toFormat("yLL");
        this.currentMonthOnly = newDate.toFormat("LL");
        this.currentYearOnly = newDate.toFormat("y");

        // Remove existing events.
        this.events = [];
        this.days = [];

        // Grab the events.
        const url = this.api + "/api/node/event?filter[event_month][value]="
          + newMonth + "&filter[event_year][value]=" + newYear;

        const eventsPromise = this.getEvents(url);

        eventsPromise.then((events) => {
          setTimeout(() => {
            this.parseEventData(events);
          }, 50);
        })

        // Add days.
        .then(() => {
          setTimeout(() => {
            this.addDays(this.events);
          }, 50);
        })

        // Check for results.
        .then(() => {
          setTimeout(() => {
            this.noResults = this.days.length > 0 ? false : true;
          }, 50);
        })

        // Disable loading.
        .then(() => {
          setTimeout(() => {
            this.$emit("disable-loading");
          }, 50);
        });
      }
    }
  }
</script>

<style scoped>
  .header {
    align-items: center;
    background-color: #00274C;
    color: #fff;
    font-size: 3rem;
    padding: 2rem;
  }

  .menu {
    flex-basis: 25%;
    font-size: 2rem;
    text-transform: uppercase;
  }

  .hamburger {
    display: inline-block;
    cursor: pointer;
    transition-property: opacity, filter;
    transition-duration: 0.15s;
    transition-timing-function: linear;
    font: inherit;
    color: inherit;
    text-transform: none;
    background-color: transparent;
    border: 0;
    margin: 0;
    outline: 0;
    overflow: visible;
    padding: 0;
    position: absolute;
  }

  .hamburger-box {
    display: inline-block;
    position: relative;
  }

  .hamburger-inner {
    display: block;
  }

  .month-year {
    display: block;
    text-align: center;
  }

  .no-results {
    font-size: 3rem;
    padding: 2rem;
  }

   .month-selector-arrow {
    color: #00274C;
    position: absolute;
    text-decoration: none;
  }

  .month-selector-arrow.prev {
    left: 0;
  }

  .month-selector-arrow.next {
    right: 0;
  }

  .month-selector {
    background-color: #dadada;
    color: #00274C;
    display: flex;
    font-size: 3rem;
    justify-content: space-around;
    padding: 2rem;
    position: relative;
    text-align: center;
  }

  .day-list,
  .event-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .day {
    background-color: #7FB9DD;
    font-size: 3rem;
    padding: 1rem;
    position: sticky;
    top: -1px;
    z-index: 1;
  }

  .day-integer {
    font-weight: 700;
    padding-right: 0.5rem;
  }

  .event {
    border-bottom: 1px solid #b6b6b6;
    font-size: 1.75rem;
  }

</style>



// WEBPACK FOOTER //
// src/components/Events.vue